<template>
    <v-row no-gutters>
        <v-row no-gutters class="d-flex justify-end">
            <v-col cols="2" v-if="!actions && !auditReportMode">
                <v-btn-primary
                    block
                    @click="upsertCompany()"
                    :disabled="disabledModification"
                    v-if="!clientInvoiceMode && !hideUpsertBtn"
                    data-cy="EditCompany-valid"
                    size="large"
                >
                    {{ company.id ? "Modifier" : "Créer" }}
                </v-btn-primary>
            </v-col>
        </v-row>
        <v-row
            no-gutters
            class="d-flex justify-end"
            :style="'max-height:' + maxHeight + ';' + 'overflow:auto'"
        >
            <v-col cols="12">
                <v-card
                    class="default_card large mx-0 my-2 pa-2"
                    elevation="0"
                    id="MainInformationEditCompany"
                >
                    <v-row no-gutters>
                        <v-col cols="12" class="d-flex justify-start align-center pb-2">
                            <h2>
                                {{
                                    clientInvoiceMode
                                        ? "Information sur votre client"
                                        : "Informations sur votre entreprise"
                                }}
                            </h2>
                        </v-col>
                    </v-row>
                    <v-form v-model="isCompanyDetailsFormValid" :disabled="auditReportMode">
                        <v-row no-gutters>
                            <v-col class="d-flex pb-2 pr-2" cols="4">
                                <div class="tooltip-container">
                                    <v-text-field-filled
                                        ref="companyNameElement"
                                        v-model="company.name"
                                        :label="$t('user.companyName')"
                                        :rules="requiredRule"
                                        required
                                        :disabled="getDisabled(!adminMode && !clientInvoiceMode)"
                                        data-cy="EditCompany-name"
                                    />
                                    <v-tooltip activator="parent" v-if="$refs.companyNameElement">
                                        {{ ($refs.companyNameElement as VTextField)?.label }}
                                    </v-tooltip>
                                </div>
                            </v-col>
                            <v-col class="d-flex pb-2 pr-2" cols="4" v-if="clientInvoiceMode">
                                <div class="tooltip-container">
                                    <v-text-field-filled
                                        ref="siretElement"
                                        v-model="siret"
                                        @input="onInputSetTruncatedSiret(siret)"
                                        label="SIRET"
                                    />
                                    <v-tooltip activator="parent" v-if="$refs.siretElement">
                                        {{ ($refs.siretElement as VTextField)?.label }}
                                    </v-tooltip>
                                </div>
                            </v-col>
                            <v-col class="d-flex pb-2 pr-2" cols="4" v-if="clientInvoiceMode">
                                <div class="tooltip-container">
                                    <v-text-field-filled
                                        ref="vatNumberElement"
                                        v-model="company.vatNumber"
                                        :label="$t('settings.vatNumber')"
                                        data-cy="EditCompany-vatNumber"
                                    />
                                    <v-tooltip activator="parent" v-if="$refs.vatNumberElement">
                                        {{ ($refs.vatNumberElement as VTextField)?.label }}
                                    </v-tooltip>
                                </div>
                            </v-col>
                            <v-col
                                v-if="settingsMode || !clientInvoiceMode"
                                class="d-flex pb-2 pr-2"
                                cols="4"
                            >
                                <div class="tooltip-container">
                                    <v-text-field-filled
                                        ref="companyShortNameElement"
                                        v-model="company.shortName"
                                        :label="$t('settings.commercialName')"
                                    />
                                    <v-tooltip
                                        activator="parent"
                                        v-if="$refs.companyShortNameElement"
                                    >
                                        {{ ($refs.companyShortNameElement as VTextField)?.label }}
                                    </v-tooltip>
                                </div>
                            </v-col>
                            <v-col class="d-flex pb-2 pr-1" v-if="!clientInvoiceMode" cols="4">
                                <div class="tooltip-container">
                                    <v-autocomplete-filled
                                        ref="legalFormElement"
                                        v-model="company.legalForm"
                                        :items="legalForms"
                                        :item-title="formatTitle"
                                        item-value="id"
                                        :rules="requiredRule"
                                        required
                                        :disabled="getDisabled(!adminMode && !!company.legalForm)"
                                        :label="$t('settings.legalForm') + ' *'"
                                        data-cy="EditCompany-legalForm"
                                    />
                                    <v-tooltip activator="parent" v-if="$refs.legalFormElement">
                                        {{ ($refs.legalFormElement as VSelect)?.label }}
                                    </v-tooltip>
                                </div>
                            </v-col>
                        </v-row>
                        <v-row v-if="!clientInvoiceMode" no-gutters>
                            <v-col cols="3" class="d-flex pb-2 pr-2">
                                <div class="tooltip-container">
                                    <v-text-field-filled
                                        ref="siretElement"
                                        v-model="siret"
                                        label="SIRET *"
                                        @input="onInputSetTruncatedSiret(siret)"
                                        required
                                        :disabled="getDisabled(disabledSiret || auditReportMode)"
                                        :rules="rulesSiret"
                                        :error-messages="siretErrors"
                                        data-cy="EditCompany-siret"
                                        @update:focused="siretToSiren"
                                    />
                                    <v-tooltip activator="parent" v-if="$refs.siretElement">
                                        {{ ($refs.siretElement as VTextField)?.label }}
                                    </v-tooltip>
                                </div>
                            </v-col>
                            <v-col cols="3" class="d-flex pb-2 pr-2">
                                <div class="tooltip-container">
                                    <v-text-field-filled
                                        ref="sirenElement"
                                        label="SIREN"
                                        readonly
                                        :disabled="!adminMode"
                                        :rules="sirenRules"
                                        :model-value="company.siren"
                                    />
                                    <v-tooltip activator="parent" v-if="$refs.sirenElement">
                                        {{ ($refs.sirenElement as VTextField)?.label }}
                                    </v-tooltip>
                                </div>
                            </v-col>
                            <v-col cols="3" class="d-flex pb-2 pr-2">
                                <div class="tooltip-container">
                                    <v-text-field-filled
                                        ref="vatNumberElement"
                                        readonly
                                        :label="$t('settings.vatNumber')"
                                        :disabled="!adminMode"
                                        :model-value="company.vatNumber"
                                    />
                                    <v-tooltip activator="parent" v-if="$refs.vatNumberElement">
                                        {{ ($refs.vatNumberElement as VTextField)?.label }}
                                    </v-tooltip>
                                </div>
                            </v-col>
                            <v-col cols="3" class="d-flex pb-2 pr-1" v-if="!hideRegistrationTown">
                                <div class="tooltip-container">
                                    <v-combobox-filled
                                        ref="registrationTownElement"
                                        v-model="company.registrationTown"
                                        :items="registrationTowns"
                                        :label="$t('settings.registrationTown') + ' *'"
                                        :rules="registrationTownRules"
                                        required
                                        dense
                                    />
                                    <v-tooltip
                                        activator="parent"
                                        v-if="$refs.registrationTownElement"
                                    >
                                        {{ ($refs.registrationTownElement as VCombobox)?.label }}
                                    </v-tooltip>
                                </div>
                            </v-col>
                        </v-row>
                        <v-row no-gutters>
                            <v-col cols="4" class="d-flex pr-2">
                                <div class="tooltip-container">
                                    <v-text-field-filled
                                        ref="addressElement"
                                        v-model="company.address"
                                        :label="$t('settings.address') + ' *'"
                                        :rules="requiredRule"
                                        required
                                        data-cy="editCompany-adress"
                                        v-tool-tip="$t('settings.address') + ' *'"
                                    />
                                    <v-tooltip activator="parent" v-if="$refs.addressElement">
                                        {{ ($refs.addressElement as VTextField)?.label }}
                                    </v-tooltip>
                                </div>
                            </v-col>
                            <v-col cols="2" class="d-flex pr-2">
                                <div class="tooltip-container">
                                    <v-text-field-filled
                                        ref="postalCodeElement"
                                        v-model="company.postalCode"
                                        :label="$t('settings.postalCode') + ' *'"
                                        :rules="[
                                            ...requiredRule,
                                            (v) => v.length === 5 || '5 Caractères',
                                        ]"
                                        required
                                        data-cy="editCompany-postalCode"
                                    />
                                    <v-tooltip activator="parent" v-if="$refs.postalCodeElement">
                                        {{ ($refs.postalCodeElement as VTextField)?.label }}
                                    </v-tooltip>
                                </div>
                            </v-col>
                            <v-col cols="3" class="d-flex pr-2">
                                <div class="tooltip-container">
                                    <v-text-field-filled
                                        ref="cityElement"
                                        v-model="company.city"
                                        :label="$t('settings.city') + ' *'"
                                        :rules="requiredRule"
                                        required
                                        data-cy="editCompany-city"
                                    />
                                    <v-tooltip activator="parent" v-if="$refs.cityElement">
                                        {{ ($refs.cityElement as VTextField)?.label }}
                                    </v-tooltip>
                                </div>
                            </v-col>
                            <v-col cols="3" class="d-flex pr-1">
                                <div class="tooltip-container">
                                    <v-text-field-filled
                                        ref="countryElement"
                                        v-model="company.country"
                                        :label="$t('settings.country') + ' *'"
                                        :rules="requiredRule"
                                        required
                                        data-cy="editCompany-country"
                                    />
                                    <v-tooltip activator="parent" v-if="$refs.countryElement">
                                        {{ ($refs.countryElement as VTextField)?.label }}
                                    </v-tooltip>
                                </div>
                            </v-col>
                        </v-row>
                    </v-form>
                    <SirenCompanyDialog
                        v-model="researchDialog"
                        @close="getCompany($event)"
                        v-if="researchDialog"
                    />
                </v-card>
            </v-col>
            <v-col cols="12">
                <v-card
                    class="default_card large mx-0 my-2 pa-2"
                    elevation="0"
                    v-if="!clientInvoiceMode"
                >
                    <v-row no-gutters>
                        <v-col cols="12" class="d-flex justify-start align-center pb-2">
                            <h2>Détails de votre entreprise</h2>
                        </v-col>
                    </v-row>
                    <v-form v-model="isMainFormValid">
                        <v-row no-gutters></v-row>

                        <v-row
                            v-if="
                                (!getDisabled(!clientInvoiceMode && adminMode) &&
                                    !clientInvoiceMode) ||
                                adminMode
                            "
                            no-gutters
                            class="pb-2"
                        >
                            <v-col
                                class="d-flex pb-2 pr-2 justify-start"
                                cols="4"
                                v-if="settingsMode"
                            >
                                <v-combobox-filled
                                    v-model="activities"
                                    :items="allActivities"
                                    item-value="name"
                                    :item-title="getActivityText"
                                    label="Code NAF"
                                    chips
                                    :disabled="getDisabled(!adminMode || auditReportMode)"
                                    multiple
                                    required
                                    hide-details="auto"
                                    data-cy="EditCompany-activities"
                                    style="height: fit-content"
                                    v-tooltip="'Code NAF'"
                                >
                                    <template #chip="{ item, index }">
                                        <v-chip v-if="index < 8" class="my-1">
                                            <span>{{ item.title }}</span>
                                        </v-chip>
                                        <span
                                            v-else-if="index === 8"
                                            class="text-grey text-caption align-self-center"
                                        >
                                            +{{ activities.length - 8 }} autre(s)
                                        </span>
                                    </template>
                                </v-combobox-filled>
                            </v-col>
                            <v-col class="d-flex pb-2 pr-2" cols="3">
                                <v-select-filled
                                    v-model="company.salesAccounts"
                                    :items="availableSalesAccounts"
                                    item-value="name"
                                    :label="
                                        company.solution === 'MANAGEMENT'
                                            ? $t('settings.salesAccountingAccountManagement')
                                            : $t('settings.salesAccountingAccount')
                                    "
                                    multiple
                                    chips
                                    return-object
                                    :rules="[
                                        (v) =>
                                            v.length > 0 || $t('settings.atLeastOneItemRequired'),
                                    ]"
                                    :disabled="disabledActivityModification || auditReportMode"
                                    data-cy="EditCompany-vente"
                                    :item-title="(item) => item.description"
                                    style="height: fit-content"
                                    v-tooltip="
                                        company.solution === 'MANAGEMENT'
                                            ? $t('settings.salesAccountingAccountManagement')
                                            : $t('settings.salesAccountingAccount')
                                    "
                                >
                                    <template #chip="{ item, index }">
                                        <v-chip v-if="index < 6">
                                            <span>{{ item.title }}</span>
                                        </v-chip>
                                        <span
                                            v-else-if="index === 6"
                                            class="text-grey text-caption align-self-center"
                                        >
                                            +{{ company.salesAccounts.length - 6 }} autre(s)
                                        </span>
                                    </template>
                                </v-select-filled>
                            </v-col>
                            <v-col
                                class="d-flex pb-2 pr-1"
                                cols="5"
                                v-if="adminMode && !hideCategories"
                            >
                                <div class="tooltip-container">
                                    <v-text-field-filled
                                        ref="companyCategoriesElement"
                                        v-model="company.categories"
                                        :label="$t('settings.categories')"
                                        hide-details="auto"
                                        :disabled="auditReportMode"
                                    />
                                    <v-tooltip
                                        activator="parent"
                                        v-if="$refs.companyCategoriesElement"
                                    >
                                        {{ ($refs.companyCategoriesElement as VTextField)?.label }}
                                    </v-tooltip>
                                </div>
                            </v-col>
                        </v-row>

                        <v-row no-gutters class="py-2">
                            <v-col class="d-flex pr-2" cols="2" v-if="settingsMode">
                                <div class="tooltip-container">
                                    <oss-text-field-filled
                                        v-model="shareCapital"
                                        :label="shareCapitalLabel"
                                        :rules="requiredRule"
                                        required
                                        :disabled="auditReportMode"
                                    />
                                    <v-tooltip activator="parent">
                                        {{ shareCapitalLabel }}
                                    </v-tooltip>
                                </div>
                            </v-col>
                            <v-col class="d-flex pr-2" cols="2" v-if="settingsMode">
                                <div class="tooltip-container">
                                    <oss-text-field-filled
                                        v-model="shareTotal"
                                        :label="shareTotalLabel"
                                        :disabled="auditReportMode"
                                    />
                                    <v-tooltip activator="parent">
                                        {{ shareTotalLabel }}
                                    </v-tooltip>
                                </div>
                            </v-col>
                            <v-col class="d-flex pr-2" cols="3" v-if="settingsMode">
                                <div class="tooltip-container">
                                    <oss-text-field-filled
                                        v-model="company.salaryCount"
                                        :label="numberOfEmployeesLabel"
                                        :rules="requiredRule"
                                        required
                                        :disabled="auditReportMode"
                                    />
                                    <v-tooltip activator="parent">
                                        {{ numberOfEmployeesLabel }}
                                    </v-tooltip>
                                </div>
                            </v-col>
                            <v-col cols="5" class="d-flex justify-center">
                                <v-switch
                                    v-model="company.vehicle"
                                    :label="$t('settings.vehicle')"
                                    class="d-flex pr-2 mt-1"
                                    v-if="!getDisabled(!adminMode)"
                                />
                                <v-switch
                                    v-model="company.shop"
                                    :label="$t('settings.withStore')"
                                    class="d-flex pr-2 mt-1"
                                    v-if="!getDisabled(!adminMode)"
                                    data-cy="EditCompany-caisse"
                                />
                            </v-col>
                        </v-row>

                        <v-row no-gutters>
                            <v-col cols="4">
                                <div class="tooltip-container">
                                    <v-combobox-filled
                                        ref="timeZoneElement"
                                        v-model="company.timeZone"
                                        :items="timeZones"
                                        label="Fuseau horaire"
                                        dense
                                        :disabled="auditReportMode"
                                    />
                                    <v-tooltip activator="parent" v-if="$refs.timeZoneElement">
                                        {{ ($refs.timeZoneElement as VCombobox)?.label }}
                                    </v-tooltip>
                                </div>
                            </v-col>
                            <v-col cols="2">
                                <div class="tooltip-container">
                                    <v-text-field-filled
                                        ref="externalIdElement"
                                        v-model="company.externalId"
                                        label="Identifiant Externe"
                                        dense
                                        class="pl-2"
                                        :disabled="auditReportMode"
                                    />
                                    <v-tooltip activator="parent" v-if="$refs.externalIdElement">
                                        {{ ($refs.externalIdElement as VTextField)?.label }}
                                    </v-tooltip>
                                </div>
                            </v-col>
                            <v-col cols="6" v-if="isRoot">
                                <v-text-field-filled
                                    v-model="company.agirisId"
                                    maxLength="50"
                                    label="Identifiant Agiris"
                                    dense
                                    class="pl-2"
                                    :disabled="auditReportMode"
                                />
                            </v-col>
                        </v-row>
                    </v-form>
                </v-card>
            </v-col>
            <v-col cols="12" v-if="isRoot">
                <v-card class="default_card large mx-0 my-2 pa-2" elevation="0">
                    <v-row no-gutters>
                        <v-col cols="12" class="d-flex justify-start align-center pb-2">
                            <h2>Options du client bobbee (type de client / tarification,...)</h2>
                        </v-col>
                    </v-row>
                    <v-form v-model="isCompanySettingsFormValid">
                        <v-row no-gutters>
                            <v-col cols="12" class="py-2">
                                <v-row no-gutters>
                                    <v-col cols="2" class="d-flex align-center pl-3 pr-2">
                                        <v-switch
                                            v-model="company.accountingFirm"
                                            label="Société comptable"
                                            class="mt-0 pt-0"
                                            size="small"
                                            :readonly="company.bobbeePriceList?.id"
                                            @update:model-value="setBobbeePriceList(true)"
                                        />
                                    </v-col>
                                    <v-col cols="2" class="d-flex align-center pl-3">
                                        <v-switch
                                            v-model="company.enableAccountingFirms"
                                            label="Cabinets multiples"
                                            class="mt-0 pt-0"
                                            size="small"
                                        />
                                    </v-col>
                                    <v-col cols="4" class="d-flex align-center pl-3" v-if="isRoot">
                                        <div class="tooltip-container">
                                            <v-select-filled
                                                ref="clientUserAccessRightElement"
                                                v-model="company.clientUserAccessRight"
                                                :items="clientUserAccessRights"
                                                item-value="id"
                                                label="Mode d'accès"
                                                :item-title="(item) => item.name"
                                            />
                                            <v-tooltip
                                                activator="parent"
                                                v-if="$refs.clientUserAccessRightElement"
                                            >
                                                {{
                                                    ($refs.clientUserAccessRightElement as VSelect)
                                                        ?.label
                                                }}
                                            </v-tooltip>
                                        </div>
                                    </v-col>
                                    <v-col cols="4" class="d-flex align-center pl-3">
                                        <v-text-field-filled
                                            :model-value="bobbeePriceListName"
                                            label="Grille tarifaire"
                                            readonly
                                        />
                                    </v-col>
                                    <v-col
                                        v-if="bobbeePriceList?.version === 2"
                                        cols="3"
                                        class="d-flex pr-2"
                                    >
                                        <v-text-field-filled
                                            hint="Pour modifier votre offre, veuiller consulter votre comptable"
                                            :hide-details="!isAccountingClient"
                                            required
                                            disable-lookup
                                            :readonly="
                                                isAccountingClient || accountingFirm ? true : false
                                            "
                                            :label="'Type d\'offre'"
                                            :model-value="offerValue"
                                        />
                                    </v-col>
                                    <v-col v-if="bobbeePriceList?.version === 2">
                                        <oss-dialog
                                            v-model="offerDialog"
                                            v-if="company.accountingFirm"
                                            height="100vh"
                                            width="70vw"
                                        >
                                            <template v-slot:activator="{ props }">
                                                <v-btn v-if="isOfferWriteRight" v-bind="props">
                                                    Modifier
                                                </v-btn>
                                                <v-btn v-else v-bind="props"> Afficher </v-btn>
                                            </template>

                                            <div style="overflow: hidden">
                                                <v-row no-gutters>
                                                    <v-col cols="12">
                                                        <AccountingOffer heightMax="80vh">
                                                        </AccountingOffer>
                                                    </v-col>
                                                </v-row>
                                                <v-radio-group
                                                    v-model="newOfferType"
                                                    v-if="isOfferWriteRight"
                                                    hide-details
                                                    class="pt-0 mt-0"
                                                    color="primary"
                                                >
                                                    <v-row no-gutters>
                                                        <v-col cols="4" />
                                                        <v-col cols="2">
                                                            <v-radio
                                                                class="justify-center align-center"
                                                                :value="1"
                                                            />
                                                        </v-col>
                                                        <v-col cols="2">
                                                            <v-radio
                                                                class="justify-center align-center"
                                                                :value="2"
                                                            />
                                                        </v-col>
                                                        <v-col cols="2">
                                                            <v-radio
                                                                class="justify-center align-center"
                                                                :value="3"
                                                            />
                                                        </v-col>
                                                        <v-col cols="2">
                                                            <v-tooltip v-if="!companyId">
                                                                <template #activator="{ props }">
                                                                    <v-radio
                                                                        v-bind="props"
                                                                        class="justify-center align-center"
                                                                        :value="7"
                                                                    />
                                                                </template>
                                                                <span>{{
                                                                    $t(
                                                                        "training-text.description-text",
                                                                    )
                                                                }}</span>
                                                            </v-tooltip>
                                                        </v-col>
                                                    </v-row>
                                                </v-radio-group>
                                                <p v-else>
                                                    Pour modifier votre offre, veuiller consulter
                                                    votre comptable
                                                </p>
                                            </div>
                                        </oss-dialog>
                                        <oss-dialog
                                            v-model="offerDialog"
                                            v-else
                                            height="100vh"
                                            width="70vw"
                                        >
                                            <template v-slot:activator="{ props }">
                                                <v-btn v-if="isOfferWriteRight" v-bind="props">
                                                    Modifier
                                                </v-btn>
                                                <v-btn v-else v-bind="props"> Afficher </v-btn>
                                            </template>

                                            <div style="overflow: hidden">
                                                <v-row no-gutters>
                                                    <v-col cols="12">
                                                        <BusinessOffer maxHeight="80vh" />
                                                    </v-col>
                                                </v-row>
                                                <v-radio-group
                                                    v-model="newOfferType"
                                                    v-if="isOfferWriteRight"
                                                    hide-details
                                                    class="pt-0 mt-0"
                                                    color="primary"
                                                >
                                                    <v-row no-gutters>
                                                        <v-col cols="4" />
                                                        <v-col cols="2">
                                                            <v-radio
                                                                v-if="isFreemiumAccepted"
                                                                class="justify-center align-center"
                                                                :value="4"
                                                            />
                                                        </v-col>
                                                        <v-col cols="2">
                                                            <v-radio
                                                                class="justify-center align-center"
                                                                :value="8"
                                                            />
                                                        </v-col>
                                                        <v-col cols="2">
                                                            <v-radio
                                                                class="justify-center align-center"
                                                                :value="5"
                                                            />
                                                        </v-col>
                                                        <v-col cols="2">
                                                            <v-radio
                                                                class="justify-center align-center"
                                                                :value="6"
                                                            />
                                                        </v-col>
                                                    </v-row>
                                                </v-radio-group>
                                                <p v-else>
                                                    Pour modifier votre offre, veuiller consulter
                                                    votre comptable
                                                </p>
                                            </div>
                                        </oss-dialog>
                                    </v-col>
                                </v-row>
                            </v-col>
                        </v-row>
                    </v-form>
                </v-card>
            </v-col>
            <v-col cols="12" v-if="!clientInvoiceMode && settingsMode">
                <v-card class="default_card large mx-0 my-2 pa-2" elevation="0">
                    <v-row no-gutters>
                        <v-col cols="12" class="d-flex justify-start align-center pb-2">
                            <h2>Régimes d'imposition</h2>
                            <!-- {{ disabledValidate+ " - " + isTaxDetailsFormValid + " - " + isCompanyDetailsFormValid }} -->
                        </v-col>
                    </v-row>
                    <v-form
                        ref="taxDetailsForm"
                        v-model="isTaxDetailsFormValid"
                        :disabled="auditReportMode"
                    >
                        <v-row no-gutters>
                            <v-col class="d-flex pr-2" cols="2" v-if="settingsMode">
                                <div class="tooltip-container">
                                    <v-select-filled
                                        ref="accountingTypeElement"
                                        v-model="profile.accountingType"
                                        :items="accountingTypes"
                                        item-value="id"
                                        :rules="requiredRule"
                                        required
                                        :label="$t('settings.accountingType') + ' *'"
                                        :item-title="(item) => item.name"
                                        data-cy="EditCompany-accountingType"
                                    />
                                    <v-tooltip
                                        activator="parent"
                                        v-if="$refs.accountingTypeElement"
                                    >
                                        {{ ($refs.accountingTypeElement as VSelect)?.label }}
                                    </v-tooltip>
                                </div>
                            </v-col>
                            <v-col class="d-flex pr-2" cols="2" v-if="!vatSystemHide">
                                <div class="tooltip-container">
                                    <v-select-filled
                                        ref="vatSystemElement"
                                        v-model="company.vatSystem"
                                        :items="vatSystems"
                                        item-value="id"
                                        :rules="requiredRule"
                                        required
                                        :label="$t('settings.vatSystem') + ' *'"
                                        :item-title="(item) => item.name"
                                        data-cy="EditCompany-VATSystem"
                                    />
                                    <v-tooltip activator="parent" v-if="$refs.vatSystemElement">
                                        {{ ($refs.vatSystemElement as VSelect)?.label }}
                                    </v-tooltip>
                                </div>
                            </v-col>
                            <v-col class="d-flex pr-2" cols="2" v-if="!vatSystemHide">
                                <div class="tooltip-container">
                                    <v-select-filled
                                        ref="vatDeclarationDayElement"
                                        v-model="company.vatDeclarationDay"
                                        :items="vatDeclarationDays"
                                        label="Jour déclaration TVA"
                                        :disabled="
                                            company.vatSystem === 'NotSubjectTo' || auditReportMode
                                        "
                                    />
                                    <v-tooltip
                                        activator="parent"
                                        v-if="$refs.vatDeclarationDayElement"
                                    >
                                        {{ ($refs.vatDeclarationDayElement as VSelect)?.label }}
                                    </v-tooltip>
                                </div>
                            </v-col>
                            <v-col class="d-flex pr-2" cols="2" v-if="settingsMode">
                                <div class="tooltip-container">
                                    <v-select-filled
                                        ref="incomeCategoryElement"
                                        v-model="company.incomeCategory"
                                        :items="incomeCategories"
                                        @update:model-value="changeTaxSystemTypes"
                                        item-value="id"
                                        :rules="requiredRule"
                                        required
                                        :label="$t('settings.incomeCategory') + ' *'"
                                        :item-title="(item) => item.name"
                                        data-cy="EditCompany-taxSystem"
                                    />
                                    <v-tooltip activator="parent" v-if="$refs.taxSystemElement">
                                        {{ ($refs.incomeCategoryElement as VSelect)?.label }}
                                    </v-tooltip>
                                </div>
                            </v-col>
                            <v-col class="d-flex pr-2" cols="2" v-if="settingsMode">
                                <div class="tooltip-container">
                                    <v-select-filled
                                        ref="taxRegimeElement"
                                        v-model="company.taxRegime"
                                        :items="taxRegimes"
                                        @update:model-value="changeTaxSystemTypes"
                                        item-value="id"
                                        :rules="requiredRule"
                                        required
                                        :label="$t('settings.taxRegime') + ' *'"
                                        :item-title="(item) => item.name"
                                        data-cy="EditCompany-taxRegimeElement"
                                    />
                                    <v-tooltip activator="parent">
                                        {{ ($refs.taxRegimeElement as VSelect)?.label }}
                                    </v-tooltip>
                                </div>
                            </v-col>
                            <v-col class="d-flex pr-1" cols="2" v-if="settingsMode">
                                <div class="tooltip-container">
                                    <v-select-filled
                                        ref="taxSystemTypeElement"
                                        v-model="company.taxSystemType"
                                        :items="taxSystemTypes"
                                        item-value="id"
                                        :rules="requiredRule"
                                        required
                                        :label="$t('settings.taxSystemType') + ' *'"
                                        :item-title="(item) => item.name"
                                        data-cy="EditCompany-taxSystemType"
                                    />
                                    <v-tooltip activator="parent" v-if="$refs.taxSystemTypeElement">
                                        {{ ($refs.taxSystemTypeElement as VSelect)?.label }}
                                    </v-tooltip>
                                </div>
                            </v-col>
                        </v-row>
                        <v-row no-gutters class="pt-2">
                            <v-col cols="3" class="pr-2">
                                <div class="tooltip-container">
                                    <v-autocomplete-filled
                                        ref="corporateTaxDepartmentElement"
                                        v-model="company.corporateTaxDepartment"
                                        :items="sieList"
                                        :item-title="formatItemTitle"
                                        item-value="id"
                                        label="Service des impôts des entreprises"
                                    />
                                    <v-tooltip
                                        activator="parent"
                                        v-if="$refs.corporateTaxDepartmentElement"
                                    >
                                        {{
                                            ($refs.corporateTaxDepartmentElement as VAutocomplete)
                                                ?.label
                                        }}
                                    </v-tooltip>
                                </div>
                            </v-col>
                            <v-col cols="2" class="pr-2">
                                <div class="tooltip-container">
                                    <oss-date-picker-filled
                                        ref="startBusinessDateElement"
                                        v-model="company.startBusinessDate"
                                        label="Date de début d'activité"
                                        style="flex: 1"
                                        :textFieldReadOnly="false"
                                        :required="false"
                                        :notRequired="true"
                                        :disabled="auditReportMode"
                                    />
                                    <v-tooltip
                                        activator="parent"
                                        v-if="$refs.startBusinessDateElement"
                                    >
                                        {{ ($refs.startBusinessDateElement as any)?.label }}
                                    </v-tooltip>
                                </div>
                            </v-col>
                            <v-col cols="2" class="pr-2">
                                <div class="tooltip-container">
                                    <oss-date-picker-filled
                                        ref="firstAccountingPeriodEndElement"
                                        v-model="company.firstAccountingPeriodEnd"
                                        label="Date de fin du 1er exercice"
                                        style="flex: 1"
                                        :textFieldReadOnly="false"
                                        :required="false"
                                        :notRequired="true"
                                        :disabled="auditReportMode"
                                    />
                                    <v-tooltip
                                        activator="parent"
                                        v-if="$refs.firstAccountingPeriodEndElement"
                                    >
                                        {{ ($refs.firstAccountingPeriodEndElement as any)?.label }}
                                    </v-tooltip>
                                </div>
                            </v-col>
                            <v-col class="d-flex pr-1" cols="5">
                                <div class="tooltip-container">
                                    <v-select-filled
                                        ref="vatOptionElement"
                                        :disabled="
                                            company.vatSystem === 'NotSubjectTo' || auditReportMode
                                        "
                                        v-model="company.vatOption"
                                        :items="vatOptions"
                                        item-value="id"
                                        :rules="requiredRuleIfVATSubjectTo"
                                        :label="$t('settings.vatOption') + ' *'"
                                        :item-title="(item) => item.name"
                                        style="height: 20px"
                                    />
                                    <v-tooltip activator="parent" v-if="$refs.vatOptionElement">
                                        {{ ($refs.vatOptionElement as VSelect)?.label }}
                                    </v-tooltip>
                                </div>
                            </v-col>
                        </v-row>
                    </v-form>
                </v-card>
            </v-col>
            <v-col
                cols="12"
                v-if="!clientInvoiceMode && settingsMode && isAuthorizedAccountableAccountTable"
            >
                <v-card class="default_card large mx-0 my-2 pa-2" elevation="0">
                    <v-row no-gutters>
                        <v-col cols="12" class="d-flex justify-start align-center pb-2">
                            <h2>Plan comptable</h2>
                        </v-col>
                    </v-row>
                    <v-row no-gutters>
                        <v-col class="d-flex pr-2" cols="3" v-if="settingsMode">
                            <div class="tooltip-container">
                                <v-select-filled
                                    return-object
                                    ref="accountableAccountTableElement"
                                    v-model="selectedAccountableAccountTable"
                                    :items="accountableAccountTables"
                                    :rules="accountableAccountTablesRule"
                                    required
                                    :disabled="
                                        getDisabledAccountableAccountTable() || auditReportMode
                                    "
                                    :readonly="getDisabledAccountableAccountTable()"
                                    item-title="name"
                                    :label="'Plan comptable *'"
                                    data-cy="EditCompany-planComptable"
                                    :item-title="(item) => item.name"
                                />
                                <v-tooltip
                                    activator="parent"
                                    v-if="$refs.accountableAccountTableElement"
                                >
                                    {{ ($refs.accountableAccountTableElement as VSelect)?.label }}
                                </v-tooltip>
                            </div>
                        </v-col>
                    </v-row>
                </v-card>
            </v-col>
            <v-col cols="12" v-if="!isRoot && bobbeePriceList?.version === 2">
                <v-card class="default_card large mx-0 my-2 pa-2" elevation="0">
                    <v-row no-gutters>
                        <v-col cols="12" class="d-flex justify-start align-center pb-2">
                            <h2>Type d'offre</h2>
                        </v-col>
                    </v-row>
                    <v-row no-gutters class="d-flex align-center">
                        <v-col cols="3" class="d-flex pr-2">
                            <div class="tooltip-container">
                                <v-text-field-filled
                                    ref="offerElement"
                                    hint="Pour modifier votre offre, veuiller consulter votre comptable"
                                    :hide-details="!isAccountingClient"
                                    required
                                    disable-lookup
                                    :readonly="isAccountingClient || accountingFirm ? true : false"
                                    :label="'Type d\'offre'"
                                    :model-value="offerValue"
                                    :disabled="auditReportMode"
                                />
                                <v-tooltip activator="parent" v-if="$refs.offerElement">
                                    {{ ($refs.offerElement as VTextField)?.label }}
                                </v-tooltip>
                            </div>
                        </v-col>
                        <v-col>
                            <oss-dialog
                                v-model="offerDialog"
                                v-if="isAccountingClient || accountingFirm"
                                height="100vh"
                                width="70vw"
                            >
                                <template v-slot:activator="{ props }">
                                    <v-btn
                                        v-if="isOfferWriteRight"
                                        size="small"
                                        v-bind="props"
                                        :disabled="auditReportMode"
                                    >
                                        Modifier
                                    </v-btn>
                                    <v-btn
                                        v-else
                                        size="small"
                                        v-bind="props"
                                        :disabled="auditReportMode"
                                    >
                                        Afficher
                                    </v-btn>
                                </template>

                                <div style="overflow: hidden">
                                    <v-row no-gutters>
                                        <v-col cols="12">
                                            <AccountingOffer heightMax="80vh"> </AccountingOffer>
                                        </v-col>
                                    </v-row>
                                    <v-radio-group
                                        v-model="newOfferType"
                                        v-if="isOfferWriteRight"
                                        hide-details
                                        class="pt-0 mt-0"
                                        color="primary"
                                    >
                                        <v-row no-gutters>
                                            <v-col cols="4" />
                                            <v-col cols="2">
                                                <v-radio
                                                    class="justify-center align-center"
                                                    :value="1"
                                                />
                                            </v-col>
                                            <v-col cols="2">
                                                <v-radio
                                                    class="justify-center align-center"
                                                    :value="2"
                                                />
                                            </v-col>
                                            <v-col cols="2">
                                                <v-radio
                                                    class="justify-center align-center"
                                                    :value="3"
                                                />
                                            </v-col>
                                            <v-col cols="2">
                                                <v-tooltip v-if="!companyId">
                                                    <template #activator="{ props }">
                                                        <v-radio
                                                            class="justify-center align-center"
                                                            :value="7"
                                                            v-bind="props"
                                                        />
                                                    </template>
                                                    <span>{{
                                                        $t("training-text.description-text")
                                                    }}</span>
                                                </v-tooltip>
                                            </v-col>
                                        </v-row>
                                    </v-radio-group>
                                    <p v-else>
                                        Pour modifier votre offre, veuiller consulter votre
                                        comptable
                                    </p>
                                </div>
                            </oss-dialog>
                            <oss-dialog v-model="offerDialog" v-else height="100vh" width="70vw">
                                <template v-slot:activator="{ props }">
                                    <v-btn
                                        v-if="isOfferWriteRight"
                                        v-bind="props"
                                        :disabled="auditReportMode"
                                    >
                                        Modifier
                                    </v-btn>
                                    <v-btn v-else v-bind="props"> Afficher </v-btn>
                                </template>

                                <div style="overflow: hidden">
                                    <v-row no-gutters>
                                        <v-col cols="12">
                                            <BusinessOffer maxHeight="80vh" />
                                        </v-col>
                                    </v-row>
                                    <v-radio-group
                                        v-model="newOfferType"
                                        v-if="isOfferWriteRight"
                                        hide-details
                                        class="pt-0 mt-0"
                                        color="primary"
                                    >
                                        <v-row no-gutters>
                                            <v-col cols="4" />
                                            <v-col cols="2">
                                                <v-radio
                                                    v-if="isFreemiumAccepted"
                                                    class="justify-center align-center"
                                                    :value="4"
                                                />
                                            </v-col>
                                            <v-col cols="2">
                                                <v-radio
                                                    class="justify-center align-center"
                                                    :value="8"
                                                />
                                            </v-col>
                                            <v-col cols="2">
                                                <v-radio
                                                    class="justify-center align-center"
                                                    :value="5"
                                                />
                                            </v-col>
                                            <v-col cols="2">
                                                <v-radio
                                                    class="justify-center align-center"
                                                    :value="6"
                                                />
                                            </v-col>
                                        </v-row>
                                    </v-radio-group>
                                    <p v-else>
                                        Pour modifier votre offre, veuiller consulter votre
                                        comptable
                                    </p>
                                </div>
                            </oss-dialog>
                        </v-col>
                    </v-row>
                </v-card>
            </v-col>
            <v-col cols="12" v-if="!clientInvoiceMode">
                <ResourceAllocationEdition ref="resourceAllocation" :companyId="companyId" />
            </v-col>
            <v-col
                cols="12"
                :style="{ display: isAccountingUser && !clientInvoiceMode ? 'block' : 'none' }"
            >
                <!-- <v-form ref="accountingFirmClientForm" v-model="isAccountingFirmClientValid"> -->
                <AccountingFirmClientEdition
                    ref="accountingFirmClient"
                    :companyId="companyId"
                    :auditReportMode="auditReportMode"
                />
                <!-- @form-valid="handleFormValidation($event)" -->
                <!-- </v-form> -->
            </v-col>
        </v-row>
    </v-row>
</template>

<script lang="ts">
import _, { orderBy } from "lodash";
import { State2Way, Apollo } from "@/decorators";
import moment from "moment-timezone";
import gql from "graphql-tag";
import registrationTowns from "../assets/json/registrationTowns.json";
import { User } from "../store/store";
import IS_ACCOUNTING_LINES_PRESENT_ON_COMPANY from "../graphql/accountingLine/AccountingLinesPresentInCompany.gql";
import COMPANY_ACTIVITIES from "../graphql/CompanyActivities.gql";
import UPSERT_PROFILE_COMPANY from "../graphql/UpsertProfileCompany.gql";
import { checkAccessRights } from "@/services/accessRightServices";
import CANDIDATE_ACCOUNTABLE_ACCOUNT_TABLES from "../graphql/accountableAccount/CandidateAccountableAccountTables.gql";
import CREATE_COMPANY_ACCOUNTABLE_ACCOUNT_TABLE from "../graphql/accountableAccount/CreateCompanyAccountableAccountTable.gql";
import COMPANY_EXTENDED_DETAILS from "../graphql/company/CompanyExtendedDetails.gql";
import SIRET_VERIFICATION from "../graphql/company/CompanyFromSiret.gql";
import SIE_LIST from "../graphql/company/SieList.gql";
import UPSERT_COMPANY_DETAILS_MUTATION from "../graphql/company/UpsertCompany.gql";
import GET_COMPANY_OFFER from "@/graphql/company/GetCompanyOffer.gql";
import UPDATE_CLIENT_COMPANIES from "../graphql/company/UpdateCompanyClientCompanies.gql";
import { truncateInput } from "@/helpers/formInput.helper";
import { IncomeCategories, TaxRegimes, TaxSystemsTypes } from "../types/tax/taxSystems.types";
import { Offer_type } from "@/types/offer.types";
import AccountingFirmClientEdition from "./AccountingFirmClientEdition.vue";
import ResourceAllocationEdition from "./ResourceAllocationEdition.vue";
import SirenCompanyDialog from "./SirenCompanyDialog.vue";
import AccountingOffer from "./offer/AccountingOffer.vue";
import BusinessOffer from "./offer/BusinessOffer.vue";
import Logo from "./configuration/Logo.vue";
import { timeZones } from "@/services/companyServices";
import { Component, Vue, toNative, Prop, Watch, Ref } from "vue-facing-decorator";
import { FormSelectOption } from "../types/formSelectOption.types";
import { isNotSubjectToVAT } from "@/services/vat/vatHelpers.services";
import { VSelect } from "vuetify/lib/components/index.mjs";
import { getDBFormattedDate } from "../services/dateServices";
import { updateStoreUser } from "../services/userServices";
import { CompanyVatSystem, getVATSystem } from "../services/vat/vatHelpers.services";
import { InputUpsertCompanyDTO, UpsertCompanyResponse } from "@/graphql/company/UpsertCompany.dto";
// import { AvailableSalesAccounts } from "@/types/companies/availableSalesAccounts.types";
import {
    getIncomeCategoriesByTaxRegime,
    getTaxRegimesByIncomeCategory,
} from "@/services/taxIncomeFilterServices";

@Component({
    components: {
        SirenCompanyDialog,
        AccountingFirmClientEdition,
        ResourceAllocationEdition,
        AccountingOffer,
        BusinessOffer,
        Logo,
    },
    emits: ["updated"],
})
export class EditCompany extends Vue {
    @Prop({ default: false }) readonly settingsMode;
    @Prop({ default: false }) readonly adminMode;
    @Prop({ default: false }) readonly clientInvoiceMode;

    @Prop({ default: "" }) readonly companyId: string;
    @Prop({ default: undefined }) readonly inCompany: any;
    @Prop({ default: undefined }) readonly inAccountingFirm: any;
    @Prop({ default: false }) readonly hideSolution: boolean;
    @Prop({ default: false }) readonly vatSystemHide: boolean;
    @Prop({ default: false }) readonly switchesHide: boolean;
    @Prop({ default: false }) readonly hideCategories: boolean;
    @Prop({ default: false }) readonly hideRegistrationTown: boolean;
    @Prop({ default: false }) readonly hideUpsertBtn: boolean;
    @Prop({ default: null }) actions: any;
    @Prop({ default: false }) showLogo: boolean;
    @Prop({ default: false }) auditReportMode: boolean;

    @State2Way("updateOfferType", "offerType") offerType!: Offer_type;
    @Ref("accountableAccountTableElement") readonly accountableAccountTableElement!: VSelect;
    @Ref("accountingFirmClient") accountingFirmClient: any;

    isFreemiumAccepted: boolean = true;
    newOfferType: Offer_type = null;
    profile = {
        accountingType: "",
    };
    skip: boolean = false;
    researchDialog: boolean = false;
    activities = [];
    vatDeclarationDays = [15, 16, 17, 18, 19, 20, 21, 22, 23, 24];
    siret = "";
    offerDialog: boolean = false;
    initialRofs: any = [];
    legalForm = "SAS";
    /**
     * Validity of the Tax Details form. (Régimes d'imposition)
     * Computed result of all the individual input validation rules
     */
    isTaxDetailsFormValid: boolean = false;
    bobbeePriceList: any = null;

    get bobbeePriceListName() {
        return this.bobbeePriceList?.name || "";
    }

    /**
     * Validity of the Accounting Plan form. (Plan Comptable)
     * Computed result of all the individual input validation rules
     */
    // isPlanComptableFormValid: boolean = false;

    /**
     * Validity of the Company Details form
     * Computed result of all the individual input validation rules
     */
    isCompanyDetailsFormValid: boolean = false;

    /**
     * Validity of the Company Settings form
     * Computed result of all the individual input validation rules
     */
    isCompanySettingsFormValid: boolean = false;

    /**
     * Validity of the Signatory Settings form
     * Computed result of all the individual input validation rules
     */
    isSignatoryFormValid: boolean = false;

    /**
     * Tracks the overall validity of the main form
     */
    isMainFormValid: boolean = false;

    /**
     * Tracks the overall validity of the main form
     */
    // isAccountingFirmClientValid: boolean = false;

    /**
     * Required rule : Validation will not return true until this is filled
     */
    get requiredRule() {
        return [(v: any) => !!v || this.$t("settings.itemRequired")];
    }

    validateSelect() {
        if (this.accountableAccountTableElement) {
            this.accountableAccountTableElement.validate();
        }
    }

    @Watch("getDisabledAccountableAccountTable", { immediate: true })
    onDisabledAccountableAccountTableChanged() {
        this.validateSelect();
    }

    get accountableAccountTablesRule() {
        return [
            (v) => !!v || this.$t("settings.itemRequired"),
            () =>
                !this.isAccountingLinesPresentOnCompany ||
                "Plan comptable non modifiable si des écritures sont présentes dans le dossier",
        ];
    }

    /**
     * Since the VAT subject has a conditional rule, we need to remove the validation if it's not subjected to VAT
     */
    get requiredRuleIfVATSubjectTo() {
        return [];
    }

    /**
     * Rules for validation for siret number : required + should match siret format
     */
    get rulesSiret() {
        return [
            (v) => !!v || this.$t("settings.itemRequired"),
            (v) => !!(v || "").match(/^[0-9]{14}$/) || this.$t("settings.isNotSiret"),
        ];
    }

    /**
     * Rules for validation for siren number : required + should match 9 characters
     */
    get sirenRules() {
        return [
            (v: string) => !!v || this.$t("settings.itemRequired"),
            (v: string) => (v || "").length <= 9 || this.$t("settings.tooLong"),
            (v: string) => (v || "").length >= 9 || this.$t("settings.tooShort"),
        ];
    }

    /**
     * Rules for validation for registration town : required
     */
    get registrationTownRules() {
        return [
            (v: any) =>
                this.needRegistrationTown ? !!v || this.$t("settings.itemRequired") : true,
        ];
    }

    get companyAccountingFirm() {
        return this.$store.state.companyAccountingFirm;
    }

    get timeZones() {
        return timeZones;
    }

    get isAccountingClient() {
        return this.$store.state.user.isAccountingClient;
    }

    get isOfferWriteRight() {
        return this.$store.state.user?.profile?.configuration?.offer?.write;
    }

    get offerItems() {
        if (this.accountingFirm || this.isAccountingClient) {
            return [
                { text: "Cabinet démarrage", value: Offer_type.ACCOUNTING_STARTER },
                { text: "Cabinet BASIQUE", value: Offer_type.ACCOUNTING_BASIC },
                { text: "Cabinet ESSENTIEL", value: Offer_type.ACCOUNTING_ESSENTIAL },
                { text: "Cabinet PREMIUM", value: Offer_type.ACCOUNTING_PREMIUM },
                { text: "Cabinet TRAINING", value: Offer_type.TRAINING },
            ];
        } else {
            return [
                { text: "Entreprise FREEMIUM", value: Offer_type.BUSINESS_BASIC },
                { text: "Entreprise SMART", value: Offer_type.BUSINESS_SMART },
                { text: "Entreprise ESSENTIEL", value: Offer_type.BUSINESS_ESSENTIAL },
                { text: "Entreprise PREMIUM", value: Offer_type.BUSINESS_PREMIUM },
            ];
        }
    }

    get offerValue() {
        switch (this.newOfferType) {
            case Offer_type.ACCOUNTING_STARTER:
                return "Cabinet démarrage";
            case Offer_type.ACCOUNTING_BASIC:
                return "Cabinet STANDARD";
            case Offer_type.ACCOUNTING_ESSENTIAL:
                return "Cabinet ESSENTIEL";
            case Offer_type.ACCOUNTING_PREMIUM:
                return "Cabinet PREMIUM";
            case Offer_type.BUSINESS_BASIC:
                return "Entreprise FREEMIUM";
            case Offer_type.BUSINESS_SMART:
                return "Entreprise SMART";
            case Offer_type.BUSINESS_ESSENTIAL:
                return "Entreprise ESSENTIEL";
            case Offer_type.BUSINESS_PREMIUM:
                return "Entreprise PREMIUM";
            case Offer_type.TRAINING:
                return "Cabinet TRAINING";
            default:
                return "";
        }
    }

    /**
     * Determines if the Accountable Account Table should be disabled.
     *
     * @returns {boolean} - Returns `true` if the table should be disabled
     *
     * The input is enabled if:
     * - `adminMode` and `settingsMode` are both `true`, and `companyId` is not defined : Case when the user is creating a new company.
     * - `isAccountingLinesPresentOnCompany` is `false`.
     * - `company` and `company.id` are defined, and `selectedAccountableAccountTable.id` is defined. (editing an existing company)
     */
    getDisabledAccountableAccountTable() {
        if (this.adminMode && this.settingsMode && !this.companyId) {
            return false;
        }

        if (this.isAccountingLinesPresentOnCompany) {
            return true;
        }

        if (!!this.company && !!this.company?.id && !!this.selectedAccountableAccountTable?.id) {
            return false;
        } else {
            return true;
        }
    }

    @Apollo({
        query: IS_ACCOUNTING_LINES_PRESENT_ON_COMPANY,
        variables() {
            return {
                companyId: this.companyId,
            };
        },
        skip() {
            return !this.companyId;
        },
        debounce: 500,
        specificResult({ data, loading, networkStatus }: any) {
            if (data) {
                this.isAccountingLinesPresentOnCompany = data.accountingLinesPresentWithCompany;
                //Validation of the form to update with error if not possible
                this.validateSelect();
            }
        },
    })
    isAccountingLinesPresentOnCompany: boolean = false;

    /*
     * Check if the company is not subject to VAT
     * getter for reactive update if the company VAT system changes
     */
    get isNotSubjectToVAT() {
        return isNotSubjectToVAT(this.company.vatSystem);
    }

    getOfferStringValue(stringEnum) {
        switch (stringEnum) {
            case "ACCOUNTING_STARTER":
                return Offer_type.ACCOUNTING_STARTER;
            case "ACCOUNTING_BASIC":
                return Offer_type.ACCOUNTING_BASIC;
            case "ACCOUNTING_ESSENTIAL":
                return Offer_type.ACCOUNTING_ESSENTIAL;
            case "ACCOUNTING_PREMIUM":
                return Offer_type.ACCOUNTING_PREMIUM;
            case "BUSINESS_BASIC":
                return Offer_type.BUSINESS_BASIC;
            case "BUSINESS_SMART":
                return Offer_type.BUSINESS_SMART;
            case "BUSINESS_ESSENTIAL":
                return Offer_type.BUSINESS_ESSENTIAL;
            case "BUSINESS_PREMIUM":
                return Offer_type.BUSINESS_PREMIUM;
            case "TRAINING":
                return Offer_type.TRAINING;
            default:
                return Offer_type.ACCOUNTING_STARTER;
        }
    }

    clientUserAccessRights: any = [
        { id: "AVAILABLE_COMPANY", name: "Selection de dossiers" },
        { id: "AVAILABLE_WORKSPACE", name: "Groupe de dossiers" },
    ];

    legalForms: any = [
        { id: "ASSO", name: "Association" },
        { id: "CE", name: "Comité d'entreprise" },
        { id: "CL", name: "Collectivité locale" },
        { id: "CNE", name: "Commune" },
        { id: "COOP", name: "Coopérative" },
        { id: "CUMA", name: "Coopérative d'utilisation de matériel agricole" },
        { id: "DPT", name: "Département" },
        { id: "EARL", name: "Exploitation agricole à responsabilité limitée" },
        { id: "EC", name: "Entreprise collective" },
        { id: "EI", name: "Entreprise individuelle" },
        { id: "EIRL", name: "Entreprise individuelle à responsabilité limitée" },
        { id: "ETRANGER", name: "Personne morale de droit étranger" },
        { id: "EURL", name: "Entreprise unipersonnelle à responsabilité limitée" },
        { id: "FEDE", name: "Fédération" },
        { id: "FOND", name: "Fondation" },
        { id: "GAEC", name: "Groupement agricole d'exploitation en commun" },
        { id: "GEIE", name: "Groupement européen d'intérêt économique" },
        { id: "GF", name: "Groupement forestier" },
        { id: "GFA", name: "Groupement foncier agricole" },
        { id: "GFR", name: "Groupement foncier rural" },
        { id: "GIE", name: "Groupement d'intérêt économique" },
        { id: "GPT", name: "Groupement" },
        { id: "GREFFE", name: "Greffe" },
        { id: "IND", name: "Indivision" },
        { id: "MUT", name: "Mutuelle" },
        { id: "SA", name: "Société anonyme" },
        { id: "SAE", name: "Société anonyme d'exploitation" },
        { id: "SARL", name: "Société à responsabilité limitée" },
        { id: "SELARL", name: "Société d'exercice libéral à responsabilité limitée" },
        { id: "SAS", name: "Société anonyme par actions simplifiée" },
        { id: "SASU", name: "SASU" },
        { id: "SC", name: "Société civile (autre que infra)" },
        { id: "SCA", name: "Société en commandite par actions" },
        { id: "SCCV", name: "Société civile de construction vente" },
        { id: "SCE", name: "Société civile d'exploitation" },
        { id: "SCEA", name: "Société civile d'exploitation agricole" },
        { id: "SCEV", name: "Société civile d'exploitation vinicole" },
        { id: "SCH", name: "Société civile holding" },
        { id: "SCI", name: "Société civile immobilière" },
        { id: "SCM", name: "Société civile de moyens" },
        { id: "SCP", name: "Société civile professionnelle" },
        { id: "SCPG", name: "Société civile participation gestion" },
        { id: "SCPI", name: "Société civile de placement collectif immobilier" },
        { id: "SCS", name: "Société en commandite simple" },
        { id: "SCVV", name: "Société civile de vente volontaire" },
        { id: "SDE", name: "Société de droit étranger" },
        { id: "SNC", name: "Société en nom collectif" },
    ];

    allIncomeCategories: FormSelectOption[] = IncomeCategories;
    allTaxRegimes: FormSelectOption[] = TaxRegimes;
    taxSystemTypes: FormSelectOption[] = TaxSystemsTypes;

    availableSalesAccounts = [
        { name: "701", description: "Ventes de produits finis" },
        { name: "702", description: "Ventes de produits intermédiaires" },
        { name: "704", description: "Travaux" },
        { name: "705", description: "Études" },
        { name: "706", description: "Prestations de services" },
        { name: "707", description: "Ventes de marchandises" },
        { name: "708", description: "Produits des activités annexes" },
        {
            name: "7081",
            description: "Produits des services exploités dans l'intérêt du personnel",
        },
        { name: "7082", description: "Commissions et courtages" },
        { name: "7083", description: "Locations diverses" },
        { name: "7084", description: "Mise à disposition de personnel facturée" },
        { name: "7085", description: "Frais de port et livraison" },
        { name: "7086", description: "Bonis sur reprises d'emballage consignés" },
        { name: "7087", description: "Bonifications obtenues des clients et primes sur ventes" },
        { name: "7088", description: "Autres produits d'activités annexes" },
        { name: "740000", description: "Subventions" },
    ];

    accountingTypes: any = [
        { id: "RECEIVABLES_DEBT", name: "Comptabilité d'engagement" },
        { id: "INCOMING_OUTGOING_PAYMENT", name: "Comptabilité de trésorerie" },
    ];

    vatSystems: any = [
        { id: CompanyVatSystem.NotSubjectTo, name: getVATSystem(CompanyVatSystem.NotSubjectTo) },
        {
            id: CompanyVatSystem.BelowTheThreshold,
            name: getVATSystem(CompanyVatSystem.BelowTheThreshold),
        },
        { id: CompanyVatSystem.Normal, name: getVATSystem(CompanyVatSystem.Normal) },
        { id: CompanyVatSystem.Quartely, name: getVATSystem(CompanyVatSystem.Quartely) },
        { id: CompanyVatSystem.Simplified, name: getVATSystem(CompanyVatSystem.Simplified) },
        {
            id: CompanyVatSystem.SimplifiedExercise,
            name: getVATSystem(CompanyVatSystem.SimplifiedExercise),
        },
        {
            id: CompanyVatSystem.FlatRateRefund,
            name: getVATSystem(CompanyVatSystem.FlatRateRefund),
        },
    ];

    taxSystems: any = [
        { id: "BIC_Normal", name: "BIC réel normal" }, // industrial and commercial profits
        { id: "BIC_Simplified", name: "BIC réel simplifié" }, // industrial and commercial profits
        { id: "BNC", name: "BNC" }, // bénéfice non commerciaux
        // { id: "SCI", name: "SCI" } // OBSOLETE Société civile immobilière
        { id: "RF", name: "Revenus fonciers" },
    ];

    vatOptions: any = [
        { id: null, name: "N/C" },
        { id: "Debits", name: "Débits" },
        { id: "Receipts", name: "Encaissements" },
    ];

    solutions: any = [
        { id: "NO_SOLUTION", text: "pas de solution" },
        { id: "ACCOUNTING", text: "ComptaOn" },
        { id: "MANAGEMENT", text: "GestOn" },
    ];

    defaultCompany = {
        id: "",
        name: "",
        shortName: "",
        siret: "",
        siren: "",
        vatNumber: "",
        legalForm: "SAS",
        legalFormCategory: undefined,
        isActive: true,
        vatSystem: "",
        vatDeclarationDay: null,
        taxSystem: "",
        taxSystemType: "",
        withManager: true,
        vehicle: false,
        shop: false,
        salaryCount: 0,
        shareCapital: 0,
        shareTotal: 0,
        registrationTown: "",
        activities: [],
        address: "",
        postalCode: "",
        city: "",
        country: "",
        tags: [],
        clientCompanies: [],
        salesAccounts: [],
        categories: "",
        corporateTaxDepartment: "",
        timeZone: "Europe/Paris",
        externalId: "",
        profile: {
            id: "",
            createZTillReceipt: false,
            zTillReceiptWithTransactions: false,
            createExpenseReport: true,
            createPaySlip: true,
            taxeApprentissage: false,
            taxeSurLesSalaires: false,
            contributionUniqueALaFormation: false,
            contributionEffortConstruction: false,
            accountingType: "",
            paymentCreditCount: 0,
        },

        solution: "NO_SOLUTION",
        accountingFirm: false,
        enableAccountingFirms: false,
        clientUserAccessRight: "AVAILABLE_COMPANY",
        accountingFirms: [],

        startBusinessDate: "",
        firstAccountingPeriodEnd: "",
        accountableAccountTable: null,
        incomeCategory: "",
        taxRegime: "",
    };

    onInputSetTruncatedSiret(siret: string) {
        this.siret = truncateInput(siret, 14);
    }
    formatDate(date) {
        if (!date) return null;
        return moment(date).format("DD/MM/YYYY");
    }
    get disabled() {
        return !!this.companyId;
    }

    get registrationTowns() {
        return registrationTowns;
    }

    get isAgriculturalCompany() {
        return !!this.company.activities?.find((a) => a.name === "0113Z");
    }

    get corporateTaxDepartmentSelected() {
        const corporateTaxDepartmentSelected = this.sieList.find(
            (x) => x.id === this.company.corporateTaxDepartment,
        );
        if (corporateTaxDepartmentSelected) {
            return corporateTaxDepartmentSelected;
        }
        return { name: "", city: "", postalCode: "", adress: "" };
    }

    get isGestOn() {
        return this.company.solution === "MANAGEMENT";
    }

    getActivityText(activity) {
        if (activity) {
            let activityName = "";
            let description = "";
            if (typeof activity === "string") {
                activityName = activity;
            } else {
                activityName = activity.name || "XXX";
                if (activity.description) {
                    description = activity.description;
                }
            }
            if (!description) {
                const aa = this.allActivities?.find((aa) => aa.name === activityName);
                if (aa?.description) {
                    description = aa.description;
                }
            }
            let text = activityName;
            if (description) {
                text += " - " + description;
            }
            return text;
        }
        return "";
    }

    getDisabled(conditions) {
        if (this.isGestOn) {
            return false;
        }
        return conditions;
    }

    /**
     * @description This variable holds the selected accountable account table in v-select
     */
    selectedAccountableAccountTable = null;

    get accountableAccountTable() {
        if (this.company?.accountableAccountTable?.id) {
            return this.company.accountableAccountTable;
        }
        return this.selectedAccountableAccountTable;
    }

    get accountableAccountTableId() {
        if (this.accountableAccountTable) {
            return this.accountableAccountTable.id;
        }
        return null;
    }

    get shareCapital() {
        return this.company.shareCapital || 0;
    }

    set shareCapital(value) {
        this.company.shareCapital = this.convertToFloat(value);
    }

    get shareTotal() {
        return this.company.shareTotal || 0;
    }

    set shareTotal(value) {
        this.company.shareTotal = this.convertToFloat(value);
    }

    get incomeCategories() {
        return getIncomeCategoriesByTaxRegime(this.allIncomeCategories, this.company.taxRegime);
    }

    get taxRegimes() {
        return getTaxRegimesByIncomeCategory(this.allTaxRegimes, this.company.incomeCategory);
    }

    @Watch("company.incomeCategory")
    onIncomeCategoryChange() {
        if (
            this.company.taxRegime &&
            !this.taxRegimes.find((tax) => tax.id === this.company.taxRegime)
        ) {
            this.company.taxRegime = null;
        }
    }

    @Watch("company.taxRegime")
    onTaxRegimeChange() {
        if (
            this.company.incomeCategory &&
            !this.incomeCategories.find((income) => income.id === this.company.incomeCategory)
        ) {
            this.company.incomeCategory = null;
        }
    }

    convertToFloat(value) {
        let _value = value;
        if (typeof _value === "string") {
            _value = _value.replace(",", ".");
        }
        const floatValue = parseFloat(_value);
        if (isNaN(floatValue)) {
            return 0;
        }
        return +floatValue;
    }

    @Apollo({
        query: CANDIDATE_ACCOUNTABLE_ACCOUNT_TABLES,
        variables() {
            return {
                companyId: this.company?.id || "",
            };
        },
        skip() {
            return this.clientInvoiceMode;
        },
        specificResult({ data, loading, networkStatus }: any) {
            if (!loading) {
                if (data && data.candidateAccountableAccountTables) {
                    this.accountableAccountTables = data.candidateAccountableAccountTables;
                    //Used to initialize the selectedAccountableAccountTable
                    this.selectedAccountableAccountTable = this.accountableAccountTables.find(
                        (x) => x.id === this.company.accountableAccountTable?.id,
                    );
                }
            }
        },
    })
    accountableAccountTables = [];

    @Apollo({
        query: COMPANY_ACTIVITIES,
        variables() {
            return {
                search: "",
            };
        },
        specificResult({ data, loading, networkStatus }: any) {
            if (!loading) {
                if (data && data.companyActivities) {
                    this.allActivities = data.companyActivities;
                }
            }
        },
    })
    allActivities: Array<any> = [];

    @Watch("siret")
    onSiretChanged(value) {
        this.siretErrors = [];
    }

    @Watch("companyId")
    reset() {
        this.company = _.cloneDeep(this.defaultCompany);
    }

    @Apollo({
        query: SIRET_VERIFICATION,
        variables() {
            return {
                siret: this.siret,
            };
        },
        debounce: 500,
        skip() {
            return !this.siret || this.siret.length !== 14;
        },
        specificResult({ data, loading, networkStatus }: any) {
            if (!loading) {
                if (data && data.companyFromSiret) {
                    this.unknowSiret =
                        !data.companyFromSiret.company || !data.companyFromSiret.company.siret;
                    this.siretStatus = data.companyFromSiret.status;
                    if (this.unknowSiret) {
                        this.siretErrors.push(this.$t("settings.isNotSiret"));
                    }
                } else {
                    this.unknowSiret = true;
                    this.siretStatus = "";
                    this.siretErrors = [];
                }
            }
        },
    })
    unknowSiret: boolean = false;

    siretStatus = "";

    siretErrors = [];

    setCompany() {
        if (this.inCompany) {
            this.initializeFromInCompany();
        } else {
            this.initializeFromRouteParams();
        }
    }

    initializeFromInCompany() {
        this.company = { ..._.cloneDeep(this.defaultCompany), ...this.inCompany };
        this.activities = this.inCompany.activities || [];
        if (this.inCompany.siret) {
            this.siret = this.inCompany.siret;
        }
        this.updateBobbeePriceList(this.inCompany);
    }

    initializeFromRouteParams() {
        const params = this.$route.params;

        this.company = {
            ...this.company,
            name: params.nameCompany || this.company.name,
            siret: params.siret || this.company.siret,
            siren: params.siren || this.company.siren,
            vatNumber: params.vatNumber || this.company.vatNumber,
            address: params.address || this.company.address,
            postalCode: params.postalCode || this.company.postalCode,
            country: params.country || this.company.country,
            city: params.city || this.company.city,
            legalForm: params.legalForm || this.company.legalForm,
            legalFormCategory: params.legalFormCategory
                ? parseInt(params.legalFormCategory as string, 10)
                : this.company.legalFormCategory,
        };

        this.updateBobbeePriceList(this.company);

        this.siret = (params.siret as string) || this.siret;
        /**
         * Since we're getting an array from the route params set with string[], we need to enforce type to make it fit this.activities
         */
        this.activities =
            params.activities && Array.isArray(params.activities) ? params.activities : [];
    }

    /**
     * Method to format item titles */
    formatItemTitle(item: { city: string; name: string; postalCode: string; address: string }) {
        if (item.city === undefined) {
            return "";
        } else {
            return `${item.city} - ${item.name} ${item.postalCode} ${item.address}`;
        }
    }

    formatTitle(item: { id: string; name: string }) {
        if (!item.id && !item.name) {
            return "";
        } else {
            return `${item.id} - ${item.name}`;
        }
    }

    get needRegistrationTown() {
        if (["ASSO", "CL", "CNE", "FEDE", "FOND", "IND", "MUT"].includes(this.company.legalForm)) {
            return false;
        }
        return true;
    }

    needToSetBobbeePriceList(bobbeePriceList, isForswitching) {
        if (isForswitching) {
            if (this.bobbeePriceList.scope === "BUSINESS") {
                if (bobbeePriceList.version === 2 && bobbeePriceList.scope === "EXPERT")
                    return true;
                return false;
            } else if (this.bobbeePriceList.scope === "EXPERT") {
                if (bobbeePriceList.version === 2 && bobbeePriceList.scope === "BUSINESS")
                    return true;
                return false;
            }
            return false;
        } else {
            if (this.companyAccountingFirm) {
                if (bobbeePriceList.version === 2 && bobbeePriceList.scope === "EXPERT")
                    return true;
                return false;
            }
            if (bobbeePriceList.version === 2 && bobbeePriceList.scope === "BUSINESS") return true;
            return false;
        }
    }

    setBobbeePriceList(isForswitching = false) {
        for (const bobbeePriceList of this.priceLists) {
            if (this.needToSetBobbeePriceList(bobbeePriceList, isForswitching)) {
                this.bobbeePriceList = bobbeePriceList;
                if (isForswitching) {
                    this.newOfferType =
                        this.bobbeePriceList.scope === "EXPERT"
                            ? Offer_type.ACCOUNTING_BASIC
                            : Offer_type.BUSINESS_BASIC;
                }
                return;
            }
        }
    }

    updateBobbeePriceList(company) {
        // bobbee expert => bobbeePriceList from companyAccountingFirm
        if (this.companyAccountingFirm?.bobbeePriceList) {
            this.bobbeePriceList = this.companyAccountingFirm.bobbeePriceList;
        } // bobbee businness => bobbeePriceList from company
        else if (company?.bobbeePriceList) {
            this.bobbeePriceList = company.bobbeePriceList;
        } else {
            // default bobbeePriceList depending on expert or businness profile
            this.setBobbeePriceList(false);
        }
    }

    /**
     * Computed property to check if the current user is authorized to read accounting data
     *
     * @returns {boolean} - Returns true if the user has read access to accounting, otherwise false
     */
    get isAuthorizedAccountableAccountTable() {
        const authorized = checkAccessRights(this.currentUser, "accounting", "read");
        return authorized;
    }

    async beforeMount() {
        this.setCompany();
        const result = await this.$apollo.query({
            query: SIE_LIST,
        });
        this.sieList = orderBy(_.cloneDeep(result.data.getSie.sie), "postalCode");
        if (this.actions && this.actions.action) {
            this.actions.action.function = this.upsertCompany;
            this.actions.action.display = true;
            this.actions.action.loading = false;
            this.actions.action.disabled = () => {
                return this.disabledValidate;
            };
            this.actions.action.title = () => {
                return this.company?.id ? "Modifier" : "Créer";
            };
        }
        debugger;
        if (this.newOfferType === null) {
            if (this.isRoot || this.isBusinessClient) {
                this.newOfferType = Offer_type.BUSINESS_BASIC;
            } else {
                this.newOfferType = Offer_type.ACCOUNTING_BASIC;
            }
        }
    }
    sieList: any[] = [];

    @Apollo({
        query: gql`
            query BobbeePriceLists {
                bobbeePriceLists(where: { deprecated: false }) {
                    id
                    name
                    version
                    type
                    scope
                }
            }
        `,

        specificResult({ data, loading, networkStatus }: any) {
            if (data.bobbeePriceLists) {
                this.priceLists = data.bobbeePriceLists;
                if (!this.bobbeePriceList) {
                    this.setBobbeePriceList(false);
                }
            } else {
                this.priceLists = [];
            }
        },
    })
    priceLists: any = [];

    @Apollo({
        query: COMPANY_EXTENDED_DETAILS,
        variables() {
            return {
                id: this.companyId || this.inCompany.id,
            };
        },
        skip() {
            return (!this.companyId && !this.inCompany?.id) || this.skip;
        },
        specificResult({ data, loading, networkStatus }: any) {
            if (!loading) {
                if (data && data.company) {
                    const company = data.company;
                    if (company.clientCompanies) {
                        for (const cc of company.clientCompanies) {
                            cc.selected = true;
                        }
                    } else {
                        company.clientCompanies = [];
                    }
                    this.siret = company.siret ? company.siret.split("_")[0] : "";
                    this.legalForm = company.legalForm || "SAS";
                    this.activities = company.activities.map((activity) => ({ ...activity }));

                    if (data.company.profile) {
                        if (data.company.profile.rofs) {
                            this.initialRofs = data.company.profile.rofs;
                        }
                        if (data.company.profile.accountingType) {
                            this.profile.accountingType = data.company.profile.accountingType;
                        }
                    }
                    this.company = company;
                    this.updateBobbeePriceList(company);
                } else if (!this.companyId) {
                    this.company = _.cloneDeep(this.defaultCompany);
                    this.updateBobbeePriceList(this.company);
                }
            }
        },
    })
    company: any = _.cloneDeep(this.defaultCompany);

    @Apollo({
        query: GET_COMPANY_OFFER,
        variables() {
            return {
                client: { id: this.companyId || this.inCompany.id },
            };
        },
        skip() {
            return (!this.companyId && !this.inCompany?.id) || this.skip;
        },
        specificResult({ data, loading, networkStatus }: any) {
            if (!loading) {
                if (data.accountingFirmClients?.length) {
                    let lastDate: Date = null;
                    let dbAccountingFirmClient = null;
                    for (const accountingFirmClient of data.accountingFirmClients) {
                        if (
                            lastDate == null ||
                            lastDate.getTime() < new Date(accountingFirmClient.updatedAt).getTime()
                        ) {
                            this.newOfferType = this.getOfferStringValue(
                                accountingFirmClient.offer,
                            );
                            if (this.newOfferType === Offer_type.BUSINESS_BASIC) {
                                this.isFreemiumAccepted = true;
                            } else {
                                this.isFreemiumAccepted = false;
                            }
                            dbAccountingFirmClient = accountingFirmClient;
                            lastDate = new Date(accountingFirmClient.updatedAt);
                        }
                    }
                    this.dbAccountingFirmClient = dbAccountingFirmClient;
                }
            }
        },
    })
    dbAccountingFirmClient = null;

    get disabledSiret() {
        if (this.isAccountingUser) return false;
        return !!this.company.siret;
    }

    isSelectedActivity(attrs) {
        return attrs.inputValue;
    }

    get offerTypeString() {
        switch (this.newOfferType) {
            case Offer_type.ACCOUNTING_STARTER:
                return "ACCOUNTING_STARTER";
            case Offer_type.ACCOUNTING_BASIC:
                return "ACCOUNTING_BASIC";
            case Offer_type.ACCOUNTING_ESSENTIAL:
                return "ACCOUNTING_ESSENTIAL";
            case Offer_type.ACCOUNTING_PREMIUM:
                return "ACCOUNTING_PREMIUM";
            case Offer_type.BUSINESS_BASIC:
                return "BUSINESS_BASIC";
            case Offer_type.BUSINESS_SMART:
                return "BUSINESS_SMART";
            case Offer_type.BUSINESS_ESSENTIAL:
                return "BUSINESS_ESSENTIAL";
            case Offer_type.BUSINESS_PREMIUM:
                return "BUSINESS_PREMIUM";
            case Offer_type.TRAINING:
                return "TRAINING";
            default:
                return "";
        }
    }

    get bundleRofPrefix() {
        switch (this.company.incomeCategory) {
            case "BIC": {
                if (this.company.taxSystemType === "IR") {
                    return "BIC";
                }
                return "IS";
            }
            case "BNC": {
                return "BNC";
            }
            case "BA": {
                return "BA";
            }
            case "RF": {
                return "RF";
            }
            default: {
                return "BIC";
            }
        }
    }

    get rofs() {
        const defaultRofs = [
            { tax: "TVA", code: "1", prefix: "TVA" },
            { tax: "IS", code: "1", prefix: "IS" },
            { tax: "CVAE", code: "1", prefix: "CVAE" },
            { tax: "RCM", code: "1", prefix: "RCM" },
            { tax: "Liasse", code: "1", prefix: this.bundleRofPrefix },
            { tax: "Intégration fiscale", code: "1", prefix: "ISGROUPE" },
        ];
        const updatedRofs = [...defaultRofs];
        if (Array.isArray(this.initialRofs)) {
            const initialRofsMap = new Map<string, string>(
                this.initialRofs.map((item) => [item.tax, item.code]),
            );
            updatedRofs.forEach((rof) => {
                if (initialRofsMap.has(rof.tax)) {
                    rof.code = initialRofsMap.get(rof.tax);
                }
            });
        }
        return updatedRofs;
    }

    get isBobbeePriceListCreationNeeded() {
        if (!this.companyAccountingFirm || this.bobbeePriceList.scope === "BUSINESS") return true;
        return false;
    }

    async upsertCompany() {
        this.skip = true;
        if (this.$store.state?.selected_company?.id) {
            this.offerType = this.newOfferType;
        }

        if (this.company.id && this.company.type === "CONSUMER") {
            this.skip = false;
            this.$emit("updated", this.company);
            this.reset();
            return;
        }
        let activities = [];

        if (this.activities.length) {
            if (typeof this.activities[0] === "string") {
                activities = this.activities.map((ca) => ({ name: ca }));
            } else {
                activities = this.activities.map((ca) => ({ name: ca.name }));
            }
        }

        let siret = this.siret;
        if (siret && this.clientInvoiceMode) {
            siret = `${siret}_tcpOf_${this.company.id}`;
        }
        //
        // only when company.accountableAccountTable exists
        //
        const connectSalesAccounts =
            this.company?.id && this.company?.accountableAccountTable?.id
                ? this.company.salesAccounts.map((ca) => ({
                      uniqueName: {
                          name: ca.name,
                          accountableAccountTableId: this.company?.accountableAccountTable?.id,
                      },
                  }))
                : [];

        let companyId = this.companyId || this.company.id || "";

        const variables: InputUpsertCompanyDTO = {
            id: companyId,
            name: this.company.name?.trim() || "",
            shortName: this.company.shortName?.trim() || "",
            siret: siret || null,
            siren: this.company.siren || this.siret?.substring(0, 9) || null,
            vatNumber: this.company.vatNumber || null,
            legalForm: this.company.legalForm,
            legalFormCategory: this.company.legalFormCategory,
            isActive: this.company.isActive,
            vatSystem: this.company.vatSystem ? this.company.vatSystem : undefined,
            vatDeclarationDay: this.company.vatDeclarationDay,
            vatOption: this.company.vatOption,
            taxSystem: this.company.taxSystem ? this.company.taxSystem : undefined,
            taxSystemType: this.company.taxSystemType ? this.company.taxSystemType : undefined,
            withManager: this.company.withManager,
            vehicle: this.company.vehicle,
            shop: this.company.shop,
            salaryCount: this.company.salaryCount,
            shareCapital: this.shareCapital,
            shareTotal: this.shareTotal,
            registrationTown: this.company.registrationTown,
            nafsCreate: activities && activities.length ? { connect: activities } : undefined,
            nafsUpdate: activities ? { set: activities } : undefined,
            address: this.company.address,
            postalCode: this.company.postalCode,
            city: this.company.city,
            country: this.company.country,
            salesAccounts: connectSalesAccounts,
            solution: this.company.solution || undefined,
            categories: this.company.categories || null,
            accountingFirm: this.company.accountingFirm,
            enableAccountingFirms: this.company.enableAccountingFirms,
            clientUserAccessRight: this.company.clientUserAccessRight
                ? this.company.clientUserAccessRight.id
                : undefined,
            corporateTaxDepartment: this.company.corporateTaxDepartment,
            startBusinessDate: this.company.startBusinessDate
                ? getDBFormattedDate(this.company.startBusinessDate)
                : null,
            firstAccountingPeriodEnd: this.company.firstAccountingPeriodEnd
                ? getDBFormattedDate(this.company.firstAccountingPeriodEnd)
                : null,
            bobbeePriceListCreate: this.isBobbeePriceListCreationNeeded
                ? { connect: { id: this.bobbeePriceList.id } }
                : undefined,
            timeZone: this.company.timeZone || undefined,
            externalId: this.company.externalId || null,
            agirisId: this.company.agirisId,
            incomeCategory: this.company.incomeCategory || null,
            taxRegime: this.company.taxRegime || null,
        };

        //Verify if there's a selectedAccountableAccountTable and connects it if available
        if (this.selectedAccountableAccountTable?.id) {
            variables.accountableAccountTable = {
                connect: {
                    id: this.selectedAccountableAccountTable.id,
                },
            };
        }
        const result: UpsertCompanyResponse = await this.$apollo.mutate({
            mutation: UPSERT_COMPANY_DETAILS_MUTATION,
            variables,
        });

        //
        // Create company accountableAccountTable from selectedAccountableAccountTable duplication
        // only à the companycreation
        //
        if (
            (!companyId || !this.company.accountableAccountTable?.id) &&
            this.settingsMode &&
            this.selectedAccountableAccountTable.id
        ) {
            try {
                await this.$apollo.mutate({
                    mutation: CREATE_COMPANY_ACCOUNTABLE_ACCOUNT_TABLE,
                    variables: {
                        companyId: result.data.upsertCompany.id,
                        referenceAccountableAccountTableId: this.selectedAccountableAccountTable.id,
                        salesAccounts: this.company.salesAccounts
                            ? this.company.salesAccounts.map((ca) => ca.name)
                            : [],
                    },
                });
            } catch (e) {
                console.error(e);
            }
        }

        const createdCompany: boolean = !companyId;

        companyId = result.data.upsertCompany.id;

        if (this.settingsMode) {
            try {
                await this.upsertProfileCompany(
                    result.data.upsertCompany.id,
                    result?.data?.upsertCompany?.profile?.id || null,
                );
            } catch (e) {
                console.error(e);
            }
        }
        if (this.accountingFirm) {
            if (this.accountingFirmClient && this.accountingFirmClient.updateAccountingFirmClient) {
                try {
                    await this.accountingFirmClient.updateAccountingFirmClient(
                        companyId,
                        this.offerTypeString,
                    );
                } catch (error) {
                    console.error(error);
                }
            }
        }

        if (createdCompany && this.bobbeePriceList.scope === "BUSINESS") {
            debugger;
            await this.$apollo.mutate({
                mutation: UPDATE_CLIENT_COMPANIES,
                variables: {
                    id: companyId,
                    connectClientCompanies: [companyId],
                    disconnectClientCompanies: [],
                },
            });
        }

        this.skip = false;
        this.$emit("updated", result.data.upsertCompany);
        this.reset();
        updateStoreUser(this.$apollo, this.$store, this.currentUser.id);
    }

    siretToSiren(e) {
        if (!e) {
            if (this.siret && this.siret.length === 14) {
                this.company.siren = this.siret.substring(0, 9);
                if (this.company.siren.length === 9) {
                    const key = `${
                        (12 + ((3 * parseInt(this.company.siren, 10)) % 97)) % 97
                    }`.padStart(2, "0");
                    this.company.vatNumber = "FR" + key + this.company.siren;
                } else {
                    this.company.vatNumber = "";
                }
            } else {
                this.company.siren = "";
                this.company.vatNumber = "";
            }
        }
    }

    getCompany(data) {
        if (data) {
            for (const att in data) {
                if (att && att !== "activities") {
                    this.company[att] = data[att];
                }
            }
            this.siret = data.siret;
            if (data.activities) {
                this.activities = data.activities;
            } else {
                this.activities = [];
            }
        }
    }

    @Watch("inCompany", { deep: true })
    reloadInCompany() {
        this.setCompany();
    }

    get currentUser(): User {
        return this.$store.state.user;
    }

    get isRoot() {
        return this.currentUser?.isRoot;
    }

    get isBusinessClient() {
        return this.currentUser?.isBusinessClient;
    }

    get isAccountingUser() {
        return this.currentUser?.isAccountingUser;
    }

    get accountingFirm() {
        if (this.currentUser.company.accountingFirm) {
            return this.currentUser.company.accountingFirm;
        }
        return null;
    }

    /**
     * Handles the validation of the forms at mount, and whether the "modifier"/"créer" button is disabled or not.
     */
    get disabledValidate() {
        if (!this.clientInvoiceMode) {
            if (!this.accountableAccountTableId) {
                return true;
            }
            if (!this.isTaxDetailsFormValid && !this.vatSystemHide) {
                return true;
            }
        }
        return false;
    }

    get disabledModification() {
        return this.disabledModification_();
    }

    /**
     * Returns the label, translated, for the share capital input
     */
    get shareCapitalLabel() {
        return this.$t("settings.capital-social") + " *";
    }

    /**
     * Returns the label, translated, for the share total input
     */
    get shareTotalLabel() {
        return this.$t("settings.nombre-de-parts") + " *";
    }

    /**
     * Returns the label, translated, for the number of employees input
     */
    get numberOfEmployeesLabel() {
        return this.$t("settings.numberOfEmployees") + " *";
    }

    disabledModification_() {
        if (this.adminMode) return false;
        if (this.isAccountingUser) return false;
        if (this.siretStatus === "SiretIsNotSiret") return true;
        if (this.clientInvoiceMode) {
            if (this.company.address && this.company.postalCode && this.company.city) return false;
        }
        if (!this.company.salesAccounts.length) {
            return true;
        }
        return false;
    }

    get disabledActivityModification() {
        if (this.isAccountingUser) return false;
        if (this.company.id && this.company.solution === "MANAGEMENT") return false;
        return true;
    }

    get maxHeight() {
        if (this.adminMode) {
            return "calc(100vh - 140px)";
        }
        return "calc(100vh - 200px)";
        const innHeight = window.innerHeight;
        const vhHeight = (170 / innHeight) * 100;
        const maxHeight_vh = 100 - vhHeight;

        return maxHeight_vh + "vh";
    }

    changeTaxSystemTypes() {
        if (this.company.incomeCategory === "BIC") {
            this.company.taxSystemType = "IS";
        } else {
            this.company.taxSystemType = "IR";
        }
    }

    async upsertProfileCompany(companyId, profileId) {
        await this.$apollo.mutate({
            mutation: UPSERT_PROFILE_COMPANY,
            variables: {
                accountingType: this.profile.accountingType,
                id: profileId ? profileId : "",
                companyId: companyId,
                rofs: this.rofs,
            },
        });
    }

    tickBox(attrs) {
        // return true if the item is selected
        return attrs.inputValue;
    }
}

export default toNative(EditCompany);
</script>

<style lang="scss" scoped>
.tooltip-container {
    width: 100%;
    height: 100%;
}
</style>
